/* eslint-disable jira/i18n/no-export-define-message */
import React, { useCallback, useState } from 'react';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/define-messages
import { useIntl, defineMessages } from '@atlassian/jira-intl';
import { defaultProps } from '@jira/platform__default-props/src/defaultProps.tsx';
import { DiagramArrow } from './DiagramArrow.tsx';
import { EDGE_TYPE } from './directionKeys.tsx';
import { PORT_RADIUS } from './ellipsis.tsx';
import {
	LOZENGE_BORDER_RADIUS,
	LOZENGE_HEIGHT,
	getArrowPosition,
	getLabelPosition,
	getLozengePosition,
	getLozengeTextPosition,
	getLozengeWidth,
	globalLoopedTransitionRoute,
} from './getLozengePosition.tsx';
import { truncateLabel } from './isEveryPropertyDefined.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import { type Point, type TransitionIcons, type ValidationFlag, PortIndex } from './toPortInfo.tsx';
import TransitionLabel from './TransitionLabel.tsx';
import { useStrokeColor } from './useStrokeColor.tsx';

export const messages: {
	anyLozengeText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	anyLozengeText: {
		id: 'accessible-workflow-diagram.diagram.global-loop-transition.any-lozenge-text',
		defaultMessage: 'Any',
		description:
			'Message that indicates this transition can be used from "Any" status in the workflow. This is identical to the "Any" lozenge in Global Transitions.',
	},
});

const GLOBAL_LOOP_LABEL_MAX_LENGTH = 13; // as per Workflow Designer

export interface Props {
	id: string;
	flag?: ValidationFlag;
	isHovered?: boolean;
	isImmutable?: boolean;
	isSelected?: boolean;
	isTargeted?: boolean;
	position: Point | undefined;
	label: string;
	onSelect?: (item: ItemReference | undefined) => void;
	onHover?: (item: ItemReference | undefined) => void;
	icons?: TransitionIcons[];
}

export const GlobalLoopedTransition = ({
	id,
	flag,
	isHovered = false,
	isImmutable,
	position: containerPosition,
	isSelected = false,
	isTargeted = false,
	label,
	onSelect,
	onHover,
	icons = defaultProps.emptyArray,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const stroke = useStrokeColor({ flag, isHighlighted: false, isSelected, isHovered });
	const [textRef, setTextRef] = useState<SVGTextElement | null>(null);
	const lozengeText = truncateLabel(formatMessage(messages.anyLozengeText));
	const textWidth = textRef?.getBBox?.().width || 0;

	const lozengeWidth = getLozengeWidth(textWidth);

	const onGroupClick = useCallback(() => {
		if (!isImmutable) {
			onSelect?.({ type: EDGE_TYPE, id });
		}
	}, [id, isImmutable, onSelect]);

	const onMouseEnter = useCallback(() => {
		onHover?.({ id, type: EDGE_TYPE });
	}, [id, onHover]);

	const onMouseLeave = useCallback(() => {
		onHover?.(undefined);
	}, [onHover]);

	const onTextRef = useCallback((ref: SVGTextElement | null) => {
		if (ref) {
			setTextRef(ref);
		}
	}, []);

	if (!containerPosition) {
		return null;
	}

	const lozengePosition = getLozengePosition(containerPosition);
	const lozengeTextPosition = getLozengeTextPosition(lozengePosition, lozengeWidth);
	const path = globalLoopedTransitionRoute(lozengePosition, lozengeWidth);
	const arrowPosition = getArrowPosition(lozengePosition, lozengeWidth);
	const labelPosition = getLabelPosition(containerPosition);

	return (
		<g onClick={onGroupClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
			{/* Click and hover area */}
			<path
				data-testid="accessible-workflow-diagram.interactable-path"
				fill="none"
				stroke="transparent"
				d={path}
				strokeWidth="15"
			/>
			{/* Transition line */}
			<path
				data-testid="accessible-workflow-diagram.visible-path"
				fill="none"
				stroke={stroke}
				strokeLinejoin="round"
				d={path}
				strokeWidth="2"
			/>
			<DiagramArrow
				portPosition={arrowPosition}
				stroke={stroke}
				portIndex={PortIndex.Top}
				portOffset={PORT_RADIUS}
			/>
			{/* Lozenge bg */}
			<rect
				{...lozengePosition}
				width={lozengeWidth}
				height={LOZENGE_HEIGHT}
				rx={LOZENGE_BORDER_RADIUS}
				ry={LOZENGE_BORDER_RADIUS}
				fill={token('color.background.neutral.bold')}
			/>
			{/* Lozenge text */}
			<text
				{...lozengeTextPosition}
				ref={onTextRef}
				textAnchor="middle"
				fontSize="11px"
				fill={token('color.text.inverse')}
			>
				<tspan>{lozengeText}</tspan>
			</text>
			<TransitionLabel
				id={id}
				position={labelPosition}
				label={label}
				flag={flag}
				icons={icons}
				isTargeted={isTargeted}
				isSelected={isSelected}
				isHovered={isHovered}
				isImmutable={isImmutable}
				maxLabelLength={GLOBAL_LOOP_LABEL_MAX_LENGTH}
				onSelect={onSelect}
			/>
		</g>
	);
};
